import React, { Fragment, useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';

import { RESIN, METAL, RESIN_LINEUP, METAL_LINEUP, HOTTOPIC_LINEUP } from './constants';

import './BodyAir.scss';

export const BodyAir = ({ baseImgUrl }) => {
  const [linkAU2092T_2A, setLinkAU2092T_2A] = useState('/sg/en/air-au2092t-2a-c1-eyeglasses.html');
  const [linkAU2093T_2A, setLinkAU2093T_2A] = useState('/sg/en/air-au2093t-2a-c1-eyeglasses.html');
  const [linkAU2094T_2A, setLinkAU2094T_2A] = useState('/sg/en/air-au2094t-2a-c1-eyeglasses.html');

  const [linkMM1013B_3S, setLinkMM1013B_3S] = useState('/sg/en/air-mm1013b-3s-c1-eyeglasses.html');
  const [linkMM1014B_3S, setLinkMM1014B_3S] = useState('/sg/en/air-mm1014b-3s-c1-eyeglasses.html');
  const [linkMM1015B_3S, setLinkMM1015B_3S] = useState('/sg/en/air-mm1015b-3s-c1-eyeglasses.html');

  const [indexAU2092T_2A, setIndexLinkAU2092T_2A] = useState(1);
  const [indexAU2093T_2A, setIndexLinkAU2093T_2A] = useState(1);
  const [indexAU2094T_2A, setIndexLinkAU2094T_2A] = useState(1);

  const [indexMM1013B_3S, setIndexMM1013B_3S] = useState(1);
  const [indexMM1014B_3S, setIndexMM1014B_3S] = useState(1);
  const [indexMM1015B_3S, setIndexMM1015B_3S] = useState(1);

  const pinnedRef = useRef(null);
  const featuresRef = useRef(null);
  const rightRef = useRef(null);

  // State for HOTTOPIC_LINEUP selected SKUs
  const [selectedSkus, setSelectedSkus] = useState({});

  // Animated state
  const targetY = useRef(0);
  const currentY = useRef(0);
  const animationId = useRef(null);

  // Initialize selected SKUs with first SKU for each model
  useEffect(() => {
    const initialSkus = {};
    HOTTOPIC_LINEUP?.forEach(item => {
      if (item?.skus?.length > 0) {
        initialSkus[item.model] = item.skus[0].id;
      }
    });
    setSelectedSkus(initialSkus);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      // Only apply animation when window width > 767px
      if (window.innerWidth <= 767) return;

      const pinned = pinnedRef.current;
      const features = featuresRef.current;
      const right = rightRef.current;
      const rect = pinned.getBoundingClientRect();
      const progress = Math.min(
        Math.max(0, -rect.top / (pinned.offsetHeight - window.innerHeight)),
        1
      );
      targetY.current = progress * (-features.offsetHeight + right.offsetHeight); // target move distance
    };

    const animate = () => {
      // Only animate when window width > 767px
      if (window.innerWidth > 767) {
        // LERP = current + (target - current) * easing
        currentY.current += (targetY.current - currentY.current) * 0.1;
        if (featuresRef.current) {
          featuresRef.current.style.transform = `translateY(${currentY.current}px)`;
        }
        animationId.current = requestAnimationFrame(animate);
      } else if (featuresRef.current) {
        // Reset transform for mobile
        featuresRef.current.style.transform = 'translateY(0px)';
      }
    };

    const handleResize = () => {
      if (window.innerWidth <= 767) {
        // Cancel animation and reset on mobile
        if (animationId.current) {
          cancelAnimationFrame(animationId.current);
        }
        if (featuresRef.current) {
          featuresRef.current.style.transform = 'translateY(0px)';
        }
        currentY.current = 0;
        targetY.current = 0;
      } else if (!animationId.current) {
        // Restart animation on desktop
        animate();
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Start animation loop only for desktop
    if (window.innerWidth > 767) {
      animate();
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
    };
  }, []);

  // Function to handle SKU button click
  const handleSkuClick = (model, skuId) => {
    setSelectedSkus(prev => ({
      ...prev,
      [model]: skuId,
    }));
  };

  const stylesSetting = {
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    dots: false,
    fade: true,
    draggable: false,
    infinite: true,
    swipe: false,
    touchMove: false,
    autoplay: true,
    autoplaySpeed: 5000,
    speed: 800,
    pauseOnFocus: false,
    pauseOnHover: false,
  };

  const defaultLineupSetting = {
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    dots: true,
    fade: false,
    draggable: false,
    infinite: false,
    swipe: true,
    touchMove: true,
  };

  const renderLineupPagination = index => {
    return (
      <button>
        <img alt="C1" src={`${baseImgUrl}/colors/color-${index + 1}.webp`} />
      </button>
    );
  };

  const settingAU2092T_2A = {
    ...defaultLineupSetting,
    customPaging: i => renderLineupPagination(i),
    afterChange: async current => {
      await setLinkAU2092T_2A(`/sg/en/air-au2092t-2a-c${current + 1}-eyeglasses.html`);
      setIndexLinkAU2092T_2A(current + 1);
    },
  };

  const settingAU2093T_2A = {
    ...defaultLineupSetting,
    customPaging: i => renderLineupPagination(i + 8),
    afterChange: async current => {
      await setLinkAU2093T_2A(`/sg/en/air-au2093t-2a-c${current + 1}-eyeglasses.html`);
      setIndexLinkAU2093T_2A(current + 1);
    },
  };

  const settingAU2094T_2A = {
    ...defaultLineupSetting,
    customPaging: i => {
      const indexMapping = {
        0: 8,
        1: 12,
        2: 10,
        3: 13,
      };

      const index = indexMapping[i] ?? i;
      return renderLineupPagination(index);
    },
    afterChange: async current => {
      await setLinkAU2094T_2A(`/sg/en/air-au2094t-2a-c${current + 1}-eyeglasses.html`);
      setIndexLinkAU2094T_2A(current + 1);
    },
  };

  const settingMM1013B_3S = {
    ...defaultLineupSetting,
    customPaging: i => {
      const indexMapping = {
        0: 0,
        1: 4,
        2: 10,
        3: 5,
      };

      const index = indexMapping[i] ?? i;
      return renderLineupPagination(index);
    },
    afterChange: async current => {
      await setLinkMM1013B_3S(`/sg/en/air-mm1013b-3s-c${current + 1}-eyeglasses.html`);
      setIndexMM1013B_3S(current + 1);
    },
  };

  const settingMM1014B_3S = {
    ...defaultLineupSetting,
    customPaging: i => {
      const indexMapping = {
        0: 6,
        1: 10,
        2: 7,
        3: 5,
      };

      const index = indexMapping[i] ?? i;
      return renderLineupPagination(index);
    },
    afterChange: async current => {
      await setLinkMM1014B_3S(`/sg/en/air-mm1014b-3s-c${current + 1}-eyeglasses.html`);
      setIndexMM1014B_3S(current + 1);
    },
  };

  const settingMM1015B_3S = {
    ...defaultLineupSetting,
    customPaging: i => {
      const indexMapping = {
        0: 0,
        1: 14,
        2: 16,
        3: 15,
      };

      const index = indexMapping[i] ?? i;
      return renderLineupPagination(index);
    },
    afterChange: async current => {
      await setLinkMM1015B_3S(`/sg/en/air-mm1015b-3s-c${current + 1}-eyeglasses.html`);
      setIndexMM1015B_3S(current + 1);
    },
  };

  const renderStyleTopAndFeature = item => {
    return (
      <div className="body-air__styles__item">
        <div className="body-air__styles__top">
          <div className="body-air__styles__top-img">
            <Slider pa {...stylesSetting} className="body-air__styles__top-img-slider">
              {item.images?.map(({ sp, pc, alt }, index) => (
                <picture key={index}>
                  <source media="(min-width: 768px)" srcSet={`${baseImgUrl}${pc}`} />
                  <img alt={alt} src={`${baseImgUrl}${sp}`} />
                </picture>
              ))}
            </Slider>
          </div>

          <div className="body-air__styles__top-text">
            <div className="base-air__container">
              <h2 data-aos>
                {item.header.map(({ title }) => (
                  <span key={title}>{title}</span>
                ))}
              </h2>
              <div className="body-air__styles__top-text-details">
                <h3 data-aos data-aos-delay="100">
                  <span>{item.frameType}</span>
                </h3>
                <p>{item.desc}</p>
                <div className="body-air__styles__top-text-details-img">
                  <span></span>
                  <img alt={item.detail.alt} src={`${baseImgUrl}${item.detail.src}`} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="body-air__features">
          <div className="base-air__container">
            <h3>FEATURES</h3>
            <ul>
              {item.features.map(({ src, desc }, index) => (
                <li key={index} data-aos="fade-up" data-aos-delay="0">
                  <span>
                    <img alt={desc} src={`${baseImgUrl}${src}`} />
                  </span>
                  <small>#0{index + 1}</small>
                  <p>{desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="body-air__description">
          <div className="base-air__container">
            <a className="base-air__btn base-air__btn--primary" href={item.descSection.link}>
              See All Products
            </a>
            <h3 data-aos="fade-up">
              {item.descSection.title.split('\n').map((line, index) => (
                <span key={index}>
                  {line}
                  <br />
                </span>
              ))}
            </h3>
            <p data-aos data-aos-delay="600">
              {item.descSection.desc}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderLineupSlide = model => {
    const array = [1, 2, 3, 4];
    const products = array.map((_, i) => (
      <img
        key={i}
        alt={`${model} C${i + 1}`}
        src={`${baseImgUrl}/products/${model}_C${i + 1}.webp`}
      />
    ));

    const lineupSettingsMap = {
      'AU2092T-2A': settingAU2092T_2A,
      'AU2093T-2A': settingAU2093T_2A,
      'AU2094T-2A': settingAU2094T_2A,
      'MM1013B-3S': settingMM1013B_3S,
      'MM1014B-3S': settingMM1014B_3S,
      'MM1015B-3S': settingMM1015B_3S,
    };

    const lineupSetting = lineupSettingsMap[model] || defaultLineupSetting;

    return <Slider {...lineupSetting}>{products}</Slider>;
  };

  const renderLineupCards = list => {
    return list.map(({ model, delay }) => {
      const linkMap = {
        'AU2092T-2A': linkAU2092T_2A,
        'AU2093T-2A': linkAU2093T_2A,
        'AU2094T-2A': linkAU2094T_2A,
        'MM1013B-3S': linkMM1013B_3S,
        'MM1014B-3S': linkMM1014B_3S,
        'MM1015B-3S': linkMM1015B_3S,
      };

      const newLink = linkMap[model] || '';

      const indexMap = {
        'AU2092T-2A': indexAU2092T_2A,
        'AU2093T-2A': indexAU2093T_2A,
        'AU2094T-2A': indexAU2094T_2A,
        'MM1013B-3S': indexMM1013B_3S,
        'MM1014B-3S': indexMM1014B_3S,
        'MM1015B-3S': indexMM1015B_3S,
      };

      const newIndex = indexMap[model] || '';

      return (
        <li
          key={model}
          className="body-air__lineup__item"
          data-aos="fade-left"
          data-aos-delay={delay}
          data-model={`${model} C${newIndex}`}
        >
          {renderLineupSlide(model)}
          <a className="base-air__btn base-air__btn--primary" href={newLink}>
            More Details
          </a>
        </li>
      );
    });
  };

  const renderLineup = cards => {
    return (
      <section className="body-air__lineup">
        <div className="base-air__container">
          <h3>LINEUP</h3>
          <ul className="body-air__lineup__list">{renderLineupCards(cards)}</ul>
        </div>
      </section>
    );
  };

  return (
    <Fragment>
      <section className="body-air__styles" id="resin">
        {renderStyleTopAndFeature(RESIN)}
      </section>

      {renderLineup(RESIN_LINEUP)}

      <section className="body-air__styles body-air__styles--reverse" id="metal">
        {renderStyleTopAndFeature(METAL)}
      </section>

      {renderLineup(METAL_LINEUP)}

      <section ref={pinnedRef} className="body-air__hottopic">
        <div className="base-air__container">
          <div className="body-air__hottopic__wrapper">
            <h2 className="body-air__hottopic__title">Hot Topic</h2>
            <div className="body-air__hottopic__inner">
              <div className="body-air__hottopic__inner-left">
                <h3 data-aos className="body-air__hottopic__subtitle">
                  <span>
                    <span>Easy on you, all day long</span>
                  </span>
                  <span>
                    <span>Springy hinges for that just-right fit</span>
                  </span>
                </h3>
                <p className="body-air__hottopic__text">
                  Introducing the AIR series with spring hinges—flexible design that naturally
                  adapts to your face, while the pressure-absorbing structure ensures comfort even
                  during long wear.
                </p>
              </div>
              <div ref={rightRef} className="body-air__hottopic__inner-right">
                <ol ref={featuresRef} className="body-air__hottopic__features">
                  <li>
                    <img
                      alt="Elasticity of the springs makes it easier to put on and take off the spectacles"
                      loading="lazy"
                      src="https://static.lenskart.com/owndays/img/air/hot-topic-01.webp"
                    />
                    <p>
                      <i>01</i>
                      <span className="sub-t">
                        Elasticity of the springs makes it easier to put on and take off the
                        spectacles
                      </span>
                    </p>
                  </li>
                  <li>
                    <img
                      alt="Minimal pressure on temples for long-lasting comfort"
                      loading="lazy"
                      src="https://static.lenskart.com/owndays/img/air/hot-topic-02.webp"
                    />
                    <p>
                      <i>02</i>
                      <span>Minimal pressure on temples for long-lasting comfort</span>
                    </p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {HOTTOPIC_LINEUP?.length > 0 && (
        <section className="body-air__lineup">
          <div className="base-air__container">
            <h3>LINEUP</h3>
            <ul className="body-air__lineup-list">
              {HOTTOPIC_LINEUP?.map((item, index) => (
                <li
                  key={item?.model}
                  className="body-air__lineup-list-item"
                  data-aos="fade-left"
                  data-aos-delay={index * 100}
                >
                  <div className="body-air__lineup-product">
                    <span className="body-air__lineup-product-new">NEW</span>
                    <span className="body-air__lineup-product-tag">Springy hinges</span>
                    <img
                      alt={`${item?.model} ${selectedSkus[item?.model] || 'C1'}`}
                      className="body-air__lineup-product-img"
                      loading="lazy"
                      src={`https://static1.lenskart.com/owndays/img/air/products/${item?.model}_${
                        selectedSkus[item?.model] || 'C1'
                      }.webp`}
                    />
                    <div className="body-air__lineup-product-info-wrapper">
                      <div className="body-air__lineup-product-info">
                        <p className="body-air__lineup-product-model">
                          {item?.model} {selectedSkus[item?.model] || 'C1'}
                        </p>
                        <p className="body-air__lineup-product-price">
                          <small>S$</small>
                          {item?.price}
                        </p>
                      </div>
                      <div className="body-air__lineup-product-skus">
                        {item?.skus?.map(sku => (
                          <button
                            key={sku?.id}
                            className={`body-air__lineup-product-sku ${
                              selectedSkus[item?.model] === sku?.id ? 'is-active' : ''
                            }`}
                            data-sku={sku?.id}
                            style={{
                              backgroundColor: sku?.hex ?? undefined,
                              backgroundImage: sku?.img ? `url(${sku.img})` : undefined,
                            }}
                            onClick={() => handleSkuClick(item?.model, sku?.id)}
                          >
                            {sku?.id}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </Fragment>
  );
};
